import { useOutletContext } from "react-router-dom";
import { useState } from "react";
import { Download, Trash2, X, Check } from "lucide-react";
import {
  useEccRuns,
  useDeleteRun,
  useEccCalls,
  useDeleteCallJob,
} from "@/hooks/useEcc";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import type { EccArea, EccRunSummary, EccCallJob } from "@/api/types";

const STATUS_CLASS: Record<string, string> = {
  done: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  failed: "bg-destructive/15 text-destructive",
  pending: "bg-muted text-muted-foreground",
  queued: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  transcribing: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  analyzing: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  generating: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  downloading: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  fetching_cdr: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  searching: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  results_ready: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
};

const STATUS_LABEL: Record<string, string> = {
  done: "Completado",
  failed: "Fallido",
  pending: "Pendiente",
  queued: "En cola",
  transcribing: "Transcribiendo",
  analyzing: "Analizando",
  generating: "Generando",
  downloading: "Descargando",
  fetching_cdr: "Obteniendo CDR",
  searching: "Buscando",
  results_ready: "Resultados listos",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function RunRow({
  run,
  isChild = false,
  canDelete,
  onDelete,
}: {
  run: EccRunSummary;
  isChild?: boolean;
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const progress =
    run.recordings_total > 0
      ? Math.round((run.recordings_transcribed / run.recordings_total) * 100)
      : null;

  const dispatchedBy =
    isChild && run.parent_run_id
      ? `${run.started_by} (follow-up de #${run.parent_run_id.slice(0, 8)})`
      : run.started_by;

  return (
    <tr className={cn("border-b last:border-0", isChild && "bg-muted/30")}>
      <td className={cn("py-3 pr-4 text-sm font-medium", isChild ? "pl-8" : "pl-4")}>
        {isChild && <span className="mr-1 text-muted-foreground text-xs">↳</span>}
        {run.srp_name}
        {run.followup_kind && (
          <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            {run.followup_kind === "drilldown" ? "Profundización" : "Secundario"}
          </span>
        )}
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground whitespace-nowrap">
        {fmtDate(run.period_start).split(",")[0]} →{" "}
        {fmtDate(run.period_end).split(",")[0]}
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground whitespace-nowrap">
        {fmtDate(run.started_at)}
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground">
        {dispatchedBy}
      </td>
      <td className="py-3 pr-4">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-medium",
            STATUS_CLASS[run.status] ?? "bg-muted text-muted-foreground",
          )}
        >
          {STATUS_LABEL[run.status] ?? run.status}
        </span>
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground">
        {progress !== null ? `${progress}%` : "—"}
      </td>
      <td className="py-3 pr-4 text-right">
        <div className="flex items-center justify-end gap-2">
          {run.output_file && (
            <a
              href={`/api/v1/ecc/runs/${run.id}/download`}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent"
            >
              <Download className="size-3" /> Descargar
            </a>
          )}
          {canDelete && !confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              title="Borrar corrida"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          {canDelete && confirming && (
            <span className="flex items-center gap-1">
              <span className="text-xs text-destructive">¿Borrar?</span>
              <button
                type="button"
                onClick={() => { onDelete(run.id); setConfirming(false); }}
                className="rounded p-0.5 text-destructive hover:bg-destructive/10"
                title="Confirmar"
              >
                <Check className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                title="Cancelar"
              >
                <X className="size-3.5" />
              </button>
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function CallJobRow({
  job,
  canDelete,
  onDelete,
}: {
  job: EccCallJob;
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <tr className="border-b last:border-0">
      <td className="py-3 pr-4 pl-4 text-sm font-medium">{job.number}</td>
      <td className="py-3 pr-4 text-sm text-muted-foreground whitespace-nowrap">
        {fmtDate(job.period_start).split(",")[0]} →{" "}
        {fmtDate(job.period_end).split(",")[0]}
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground whitespace-nowrap">
        {fmtDate(job.started_at)}
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground">{job.started_by}</td>
      <td className="py-3 pr-4">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-medium",
            STATUS_CLASS[job.status] ?? "bg-muted text-muted-foreground",
          )}
        >
          {STATUS_LABEL[job.status] ?? job.status}
        </span>
      </td>
      <td className="py-3 pr-4 text-sm text-muted-foreground">
        {job.selected?.length ?? 0}
      </td>
      <td className="py-3 pr-4 text-right">
        <div className="flex items-center justify-end gap-2">
          {job.has_output && (
            <a
              href={`/api/v1/ecc/calls/${job.id}/file`}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors hover:bg-accent"
            >
              <Download className="size-3" /> ZIP
            </a>
          )}
          {canDelete && !confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              title="Borrar descarga"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          {canDelete && confirming && (
            <span className="flex items-center gap-1">
              <span className="text-xs text-destructive">¿Borrar?</span>
              <button
                type="button"
                onClick={() => {
                  onDelete(job.id);
                  setConfirming(false);
                }}
                className="rounded p-0.5 text-destructive hover:bg-destructive/10"
                title="Confirmar"
              >
                <Check className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                title="Cancelar"
              >
                <X className="size-3.5" />
              </button>
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

export function EccHistoryPage() {
  const { area } = useOutletContext<{ area: EccArea }>();
  const [page, setPage] = useState(1);
  const { data, isPending, error } = useEccRuns(area, page);
  const { user } = useAuth();
  const deleteRun = useDeleteRun();
  const runs = data?.items ?? [];
  const total = data?.total ?? 0;
  const perPage = data?.per_page ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const canDelete = user?.role === "admin";

  const [callsPage, setCallsPage] = useState(1);
  const calls = useEccCalls(callsPage);
  const deleteCallJob = useDeleteCallJob();
  const callItems = calls.data?.items ?? [];
  const callsTotal = calls.data?.total ?? 0;
  const callsPerPage = calls.data?.per_page ?? 20;
  const callsTotalPages = Math.max(1, Math.ceil(callsTotal / callsPerPage));

  function handleDelete(id: string) {
    deleteRun.mutate(id);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Registro inmutable de todas las corridas del área{" "}
        <span className="font-medium text-foreground">{area.toUpperCase()}</span>.
      </p>

      {isPending && !data && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded border bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Error al cargar el historial. El backend ECC aún no está activo.
        </div>
      )}

      {!isPending && runs.length === 0 && !error && (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          No hay corridas registradas para esta área.
        </div>
      )}

      {runs.length > 0 && (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-4 pl-4 font-medium">SRP</th>
                <th className="py-2 pr-4 font-medium">Periodo</th>
                <th className="py-2 pr-4 font-medium">Iniciado</th>
                <th className="py-2 pr-4 font-medium">Por</th>
                <th className="py-2 pr-4 font-medium">Estado</th>
                <th className="py-2 pr-4 font-medium">Progreso</th>
                <th className="py-2 pr-4 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {runs.map((r) => (
                <>
                  <RunRow key={r.id} run={r} canDelete={canDelete} onDelete={(id) => handleDelete(id)} />
                  {(r.children ?? []).map((child) => (
                    <RunRow key={child.id} run={child} isChild canDelete={canDelete} onDelete={(id) => handleDelete(id)} />
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-md border px-3 py-1 disabled:opacity-40"
          >
            Anterior
          </button>
          <span className="text-muted-foreground">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border px-3 py-1 disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Historial de descargas de "Buscar llamada" — global (no por área) */}
      <div className="space-y-3 border-t pt-6">
        <div>
          <h2 className="text-sm font-semibold">Descargas de llamadas</h2>
          <p className="text-xs text-muted-foreground">
            Historial de "Buscar llamada" (todas las áreas). Re-descarga el ZIP o
            bórralo.
          </p>
        </div>

        {calls.isPending && !calls.data && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded border bg-muted" />
            ))}
          </div>
        )}

        {!calls.isPending && callItems.length === 0 && (
          <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
            No hay descargas de llamadas registradas.
          </div>
        )}

        {callItems.length > 0 && (
          <div className="rounded-lg border bg-card overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-4 pl-4 font-medium">Número/Ext</th>
                  <th className="py-2 pr-4 font-medium">Periodo</th>
                  <th className="py-2 pr-4 font-medium">Iniciado</th>
                  <th className="py-2 pr-4 font-medium">Por</th>
                  <th className="py-2 pr-4 font-medium">Estado</th>
                  <th className="py-2 pr-4 font-medium">Llamadas</th>
                  <th className="py-2 pr-4 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {callItems.map((j) => (
                  <CallJobRow
                    key={j.id}
                    job={j}
                    canDelete={canDelete}
                    onDelete={(id) => deleteCallJob.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {callsTotalPages > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <button
              type="button"
              disabled={callsPage <= 1}
              onClick={() => setCallsPage((p) => p - 1)}
              className="rounded-md border px-3 py-1 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-muted-foreground">
              {callsPage} / {callsTotalPages}
            </span>
            <button
              type="button"
              disabled={callsPage >= callsTotalPages}
              onClick={() => setCallsPage((p) => p + 1)}
              className="rounded-md border px-3 py-1 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
